import { useMemo, useRef, useState } from "react";
import DynamicForm, { DynamicFormRef, setCurrencySymbolForMoneyInputs } from "@mavichpe/dynamic-forms";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faPlus, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import styles from "./style.module.scss";
import ClientShippingOptionFormConfig from "./form";
import {
    ClientShippingMode,
    ClientShippingOption,
    MAX_CLIENT_SHIPPING_OPTIONS,
    ShippingProviderLike,
    createEmptyShippingOption,
    describeShippingOption,
    generateShippingOptionId,
    hasValidThirdPartyData,
} from "@utils/ClientShipping";
import { CURRENCIES_SYMBOLS, COLON_CURRENCY_ID } from "@utils/Constants";
import { formatMoneyAmount } from "@utils/EDocument";

type ShippingOptionFormData = {
    mode: string;
    providerId: string;
    amount: number;
};

type ClientShippingOptionsEditorProps = {
    value: ClientShippingOption[];
    onChange: (next: ClientShippingOption[]) => void;
    /** Proveedores activos, para el select y para describir cada opcion. */
    providers: ShippingProviderLike[];
    maxItems?: number;
    currencySymbol?: string;
};

/**
 * Edita las formas de envio disponibles para un cliente. Son varias a proposito: un mismo
 * cliente puede recibir por distintos couriers, o por courier y por envio propio, y al
 * facturar se elige cual aplica en ese documento.
 */
const ClientShippingOptionsEditor = ({
    value,
    onChange,
    providers,
    maxItems = MAX_CLIENT_SHIPPING_OPTIONS,
    currencySymbol = "",
}: ClientShippingOptionsEditorProps) => {
    const formRef = useRef<DynamicFormRef>(null);
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const limitReached = value.length >= maxItems;
    const symbol = currencySymbol || CURRENCIES_SYMBOLS[COLON_CURRENCY_ID];
    const money = (amount: number): string => `${symbol} ${formatMoneyAmount(amount)}`;

    /** El simbolo tiene que llegar al input del monto, no solo a los textos del editor. */
    const formConfig = useMemo(() => {
        const withCurrency = setCurrencySymbolForMoneyInputs(ClientShippingOptionFormConfig, symbol);
        const providerOptions = providers
            .filter((provider) => !!provider.id)
            .reduce((acc, provider) => ({
                ...acc,
                [`${provider.id}`]: `${provider.codigo || ""} - ${provider.nombre || ""}`,
            }), {} as Record<string, string>);
        return {
            ...withCurrency,
            sections: withCurrency.sections.map((section: any) => ({
                ...section,
                fields: section.fields.map((row: any) =>
                    row.map((field: any) =>
                        field.name === "providerId" ? { ...field, options: providerOptions } : field
                    )
                ),
            })),
        };
    }, [symbol, providers]);

    const closeForm = () => {
        setIsFormOpen(false);
        setEditingId(null);
    };

    const openForm = (option: ClientShippingOption) => {
        setEditingId(option.id);
        setIsFormOpen(true);
        // El formulario se monta en este mismo render, asi que sembrarlo hay que diferirlo.
        setTimeout(() => formRef.current?.resetFormValue({
            mode: option.mode,
            providerId: option.providerId || "",
            amount: option.amount || 0,
        }), 0);
    };

    const handleSubmit = (formData: ShippingOptionFormData) => {
        const id = editingId || generateShippingOptionId();
        const mode = `${formData.mode || ""}` === "own" ? "own" : "external";
        const option: ClientShippingOption = {
            id,
            mode: mode as ClientShippingMode,
            providerId: mode === "external" ? `${formData.providerId || ""}` || undefined : undefined,
            amount: Number(formData.amount || 0),
        };
        const exists = value.some((o) => o.id === id);
        onChange(exists ? value.map((o) => (o.id === id ? option : o)) : [...value, option]);
        closeForm();
    };

    const handleDelete = (optionId: string) => {
        onChange(value.filter((option) => option.id !== optionId));
    };

    const renderOptionCard = (option: ClientShippingOption) => {
        const provider = providers.find((p) => p.id === option.providerId);
        // Un proveedor sin identificacion, o con un nombre que el XSD no admite, no sirve para
        // facturar: se avisa aqui y no cuando Hacienda rechace el comprobante con el
        // consecutivo ya quemado.
        const invalidProvider = option.mode === "external" && !hasValidThirdPartyData(provider);
        return (
            <div key={option.id} className={styles.optionCard} data-testid={`shipping-option-card-${option.id}`}>
                <div className={styles.optionMeta}>
                    <div className={styles.metaPrimary}>
                        <span className={cx(styles.badge, option.mode === "own" && styles.badgeOwn)}>
                            {option.mode === "own" ? "Propio" : "Externo"}
                        </span>
                        <span>{describeShippingOption(option, providers)}</span>
                    </div>
                    <div className={styles.metaSecondary}>
                        {option.mode === "own"
                            ? "Se factura como linea con el producto de envio del perfil"
                            : "Se cobra como otro cargo tipo 04 (cobro de un tercero)"}
                    </div>
                    {invalidProvider && (
                        <div className={styles.warning} data-testid={`shipping-option-invalid-${option.id}`}>
                            El proveedor no existe, no tiene identificacion o su nombre es muy corto para Hacienda: no se podra facturar.
                        </div>
                    )}
                </div>
                <div className={styles.optionAmount}>{money(option.amount)}</div>
                <div className={styles.optionActions}>
                    <Button
                        size="sm"
                        color="primary"
                        type="button"
                        data-testid={`shipping-option-edit-${option.id}`}
                        onClick={() => openForm(option)}
                        title="Editar opcion de envio"
                    >
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                    <Button
                        size="sm"
                        color="danger"
                        type="button"
                        data-testid={`shipping-option-delete-${option.id}`}
                        onClick={() => handleDelete(`${option.id}`)}
                        title="Eliminar opcion de envio"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.editor}>
            <div className={styles.headerRow}>
                <span className={styles.counter} data-testid="shipping-options-counter">
                    {value.length} {value.length === 1 ? "opción de envío" : "opciones de envío"}
                </span>
                <Button
                    color="success"
                    type="button"
                    data-testid="shipping-option-add"
                    onClick={() => openForm(createEmptyShippingOption())}
                    disabled={limitReached || isFormOpen}
                    title={limitReached ? `Máximo ${maxItems} opciones` : undefined}
                >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Agregar opción
                </Button>
            </div>

            {value.length === 0 ? (
                <div className={styles.emptyState}>
                    Este cliente no tiene opciones de envío configuradas.
                </div>
            ) : (
                value.map(renderOptionCard)
            )}

            {isFormOpen && (
                <div className={styles.formCard}>
                    {/*
                      * `innerForm` es obligatorio: este editor se monta dentro del
                      * formulario del cliente y un <form> anidado haria que Enter enviara
                      * la ficha en vez de agregar la opcion.
                      */}
                    <DynamicForm<ShippingOptionFormData>
                        ref={formRef}
                        innerForm={true}
                        formConfig={formConfig}
                        onSubmit={handleSubmit}
                    />
                    <div className={styles.formActions}>
                        <Button color="secondary" type="button" onClick={closeForm}>
                            <FontAwesomeIcon icon={faXmark} className="me-2" />
                            Cancelar
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            data-testid="shipping-option-save"
                            onClick={() => formRef.current?.triggerFormSubmit()}
                        >
                            {editingId && value.some((o) => o.id === editingId) ? "Actualizar" : "Agregar"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientShippingOptionsEditor;
